/**
 * Клиентская корзина F&B (UC-G.09). Серверной корзины нет — храним выбор
 * гостя в state (и зеркалим в sessionStorage, чтобы переход на «Мой счёт» и
 * обратно не терял корзину). На checkout каждая строка превращается в
 * отдельную заявку POST /v1/requests.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  catalogItemCurrency,
  catalogItemPriceMinor,
  type CatalogViewCategory,
  type CatalogViewItem,
  type CatalogViewVariant,
} from '@hsc/types';

const CART_KEY = 'hsc:fnb-cart';

/** Строка корзины — позиция меню (+ опц. вариант) с количеством. */
export interface CartLine {
  /** Стабильный ключ строки: item.id[:variant.id]. */
  key: string;
  categoryId: string;
  categoryCode: string;
  categoryName: string;
  itemId: string;
  itemName: string;
  variantId?: string;
  variantName?: string;
  /** Цена за единицу в minor units; undefined — цена не показывается. */
  unitPriceMinor?: number;
  currency?: string;
  qty: number;
}

export interface CartTarget {
  category: CatalogViewCategory;
  item: CatalogViewItem;
  variant?: CatalogViewVariant;
}

function lineKey(itemId: string, variantId?: string): string {
  return variantId !== undefined && variantId !== '' ? `${itemId}:${variantId}` : itemId;
}

function readPersisted(): CartLine[] {
  try {
    const raw = window.sessionStorage.getItem(CART_KEY);
    if (raw === null) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as CartLine[]) : [];
  } catch {
    return [];
  }
}

export interface UseCart {
  lines: CartLine[];
  count: number;
  totalMinor: number;
  /** Валюта корзины (первая встреченная) — для строки итога. */
  currency?: string;
  qtyOf: (itemId: string, variantId?: string) => number;
  add: (target: CartTarget) => void;
  setQty: (key: string, qty: number) => void;
  remove: (key: string) => void;
  clear: () => void;
}

export function useCart(): UseCart {
  const [lines, setLines] = useState<CartLine[]>(() => readPersisted());

  useEffect(() => {
    try {
      window.sessionStorage.setItem(CART_KEY, JSON.stringify(lines));
    } catch {
      /* приватный режим — игнорируем */
    }
  }, [lines]);

  const add = useCallback((target: CartTarget) => {
    const { category, item, variant } = target;
    const key = lineKey(item.id, variant?.id);
    setLines((prev) => {
      const existing = prev.find((l) => l.key === key);
      if (existing) {
        return prev.map((l) => (l.key === key ? { ...l, qty: l.qty + 1 } : l));
      }
      const itemMinor = catalogItemPriceMinor(item);
      const unit =
        variant !== undefined
          ? variant.price_minor
          : item.show_price && itemMinor !== undefined
            ? itemMinor
            : undefined;
      const cur = variant?.currency ?? catalogItemCurrency(item);
      const next: CartLine = {
        key,
        categoryId: category.id,
        categoryCode: category.code,
        categoryName: category.name,
        itemId: item.id,
        itemName: item.name,
        ...(variant !== undefined && { variantId: variant.id, variantName: variant.name }),
        ...(unit !== undefined && { unitPriceMinor: unit }),
        ...(cur !== undefined && { currency: cur }),
        qty: 1,
      };
      return [...prev, next];
    });
  }, []);

  const setQty = useCallback((key: string, qty: number) => {
    setLines((prev) =>
      qty <= 0
        ? prev.filter((l) => l.key !== key)
        : prev.map((l) => (l.key === key ? { ...l, qty } : l)),
    );
  }, []);

  const remove = useCallback((key: string) => {
    setLines((prev) => prev.filter((l) => l.key !== key));
  }, []);

  const clear = useCallback(() => setLines([]), []);

  const qtyOf = useCallback(
    (itemId: string, variantId?: string) =>
      lines.find((l) => l.key === lineKey(itemId, variantId))?.qty ?? 0,
    [lines],
  );

  const count = useMemo(() => lines.reduce((s, l) => s + l.qty, 0), [lines]);
  const totalMinor = useMemo(
    () => lines.reduce((s, l) => s + (l.unitPriceMinor ?? 0) * l.qty, 0),
    [lines],
  );
  const currency = useMemo(() => lines.find((l) => l.currency !== undefined)?.currency, [lines]);

  return {
    lines,
    count,
    totalMinor,
    ...(currency !== undefined && { currency }),
    qtyOf,
    add,
    setQty,
    remove,
    clear,
  };
}
